<section class="workspace" [class.workspace--dark]="darkMode()">
  <div class="workspace__shell">
    <app-navbar
      [appName]="'app.name' | translate"
      [moduleName]="'localization.moduleName' | translate"
      [moduleBadge]="'navbar.module1' | translate"
      logoutLink="/"
      [darkMode]="darkMode()"
      [language]="translationService.language()"
      [languages]="languages"
      (themeToggle)="toggleTheme()"
      (languageChange)="setLanguage($event)" />

    <main class="workspace__content">
      <section class="workspace__hero">
        <div class="workspace__hero-copy">
          <p class="workspace__eyebrow">{{ 'localization.eyebrow' | translate }}</p>
          <h1>{{ 'localization.title' | translate }}</h1>
          <p class="workspace__copy">
            {{ 'localization.copy' | translate }}
          </p>
        </div>
      </section>

      <section class="workspace__grid">
        <article class="panel panel--form">
          <div class="panel__toolbar">
            <span class="panel__tab panel__tab--active">{{ 'localization.form.panelLabel' | translate }}</span>
          </div>

          <form class="form-grid" [formGroup]="form" (ngSubmit)="submit()">
            <label class="field">
              <span>{{ 'localization.form.regionLabel' | translate }}</span>
              <div class="field__custom-select" clickOutside (clickOutside)="closeRegionMenu()">
                <button
                  type="button"
                  class="field__select-trigger"
                  [class.field__input--invalid]="hasError('region')"
                  [class.field__select-trigger--placeholder]="!form.controls.region.value"
                  [attr.aria-expanded]="regionMenuOpen"
                  aria-haspopup="listbox"
                  (click)="toggleRegionMenu()">
                  <span>{{ form.controls.region.value || ('localization.form.regionPlaceholder' | translate) }}</span>
                </button>

                @if (regionMenuOpen) {
                  <div class="field__dropdown field__dropdown--select" role="listbox" aria-label="Region options">
                    @for (item of regionOptions; track item) {
                      <button type="button" class="field__dropdown-item" (click)="applyRegionOption(item)">
                        {{ item }}
                      </button>
                    }
                  </div>
                }
              </div>
              @if (hasError('region')) {
                <small class="field__error">Region is required.</small>
              }
            </label>

            <label class="field">
              <span>{{ 'localization.form.municipalityLabel' | translate }}</span>
              <div class="field__search">
                <input
                  type="text"
                  formControlName="municipality"
                  [placeholder]="'localization.form.municipalityPlaceholder' | translate"
                  [class.field__input--invalid]="hasError('municipality')" />
                @if (municipalitySuggestions.length) {
                  <div class="field__dropdown">
                    @for (item of municipalitySuggestions; track item.code) {
                      <button type="button" class="field__dropdown-item" (click)="applyMunicipalityOption(item.name)">
                        {{ item.name }}
                      </button>
                    }
                  </div>
                }
              </div>
              @if (hasError('municipality')) {
                <small class="field__error">Municipality is required.</small>
              }
              @if (isSearchingMunicipalities) {
                <small class="field__hint">{{ 'localization.form.municipalitySearching' | translate }}</small>
              }
            </label>

            <label class="field">
              <span>{{ 'localization.form.valuationDateLabel' | translate }}</span>
              <app-date-picker
                formControlName="valuationDate"
                [placeholder]="'localization.form.valuationDatePlaceholder' | translate"
                [invalid]="hasError('valuationDate')" />
              @if (hasError('valuationDate')) {
                <small class="field__error">Valuation date is required.</small>
              }
            </label>

            <label class="field field--wide">
              <span>{{ 'localization.form.cadastralLabel' | translate }}</span>
              <div class="field__search">
                <input
                  type="text"
                  formControlName="cadastralArea"
                  [placeholder]="'localization.form.cadastralPlaceholder' | translate"
                  [class.field__input--invalid]="hasError('cadastralArea')" />
                @if (cadastralSuggestions.length) {
                  <div class="field__dropdown">
                    @for (item of cadastralSuggestions; track item.code) {
                      <button type="button" class="field__dropdown-item" (click)="applyCadastralOption(item.name)">
                        {{ item.name }}
                      </button>
                    }
                  </div>
                }
              </div>
              @if (hasError('cadastralArea')) {
                <small class="field__error">Cadastral area is required.</small>
              }
              <small class="field__hint">{{ 'localization.form.cadastralHint' | translate }}</small>
              @if (isSearchingCadastralAreas) {
                <small class="field__hint">{{ 'localization.form.cadastralSearching' | translate }}</small>
              }
            </label>

            @if (errorMessage) {
              <p class="form-status form-status--error">{{ errorMessage }}</p>
            }

            <div class="form-grid__actions">
              <button type="submit" class="form-grid__submit" [disabled]="isSubmitting">
                {{ isSubmitting ? ('localization.form.submitting' | translate) : ('localization.form.submit' | translate) }}
              </button>
            </div>
          </form>
        </article>

        <section class="panel panel--output">
          <div class="panel__toolbar">
            <span class="panel__tab panel__tab--active">{{ 'localization.output.panelLabel' | translate }}</span>
          </div>

          <div class="document-preview">
            <div class="document-preview__header">
              <div class="document-preview__dots">
                <span class="document-preview__dot"></span>
                <span class="document-preview__dot"></span>
                <span class="document-preview__dot"></span>
              </div>
            </div>

            <pre>{{ previewText() }}</pre>

            <div class="document-preview__footer">
              @if (copyStatus === 'success') {
                <span class="document-preview__status">Copied</span>
              } @else if (copyStatus === 'error') {
                <span class="document-preview__status document-preview__status--error">Copy failed</span>
              } @else {
                <span class="document-preview__status">Copy all output</span>
              }

              <button
                type="button"
                class="document-preview__copy"
                (click)="copyPreviewText()"
                [disabled]="!response && !isSubmitting"
                aria-label="Copy output text">
                <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <path fill="currentColor" d="M8 7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v9h-2V7h-8V5Z" />
                  <path fill="currentColor" d="M6 9a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9Zm2 0v8h8V9H8Z" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </section>
    </main>

    <app-footer [darkMode]="darkMode()" />
  </div>
</section>
